import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { StatusBadge } from "@/components/StatusBadge";
import { ClientForm } from "@/components/ClientForm";
import { ItemsEditor } from "@/components/ItemsEditor";
import { TotalsCard } from "@/components/TotalsCard";
import { ContractSection } from "@/components/ContractSection";

const FIELDS = ["client_name", "client_phone", "client_address", "date", "time", "pickup_date", "pickup_time", "notes", "items", "transport_cost", "assigned_to", "floor", "lat", "lng"];

export default function AppointmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appt, setAppt] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pct, setPct] = useState(50);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: s }] = await Promise.all([api.get(`/appointments/${id}`), api.get("/settings")]);
      setAppt(data);
      setForm(Object.fromEntries(FIELDS.map((k) => [k, data[k] ?? (k === "lat" || k === "lng" ? null : "")])));
      setPct(data.signature?.client_percentage ?? s.client_percentage);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!appt || !form) return <div className="text-[var(--muted)]" data-testid="appointment-loading">Caricamento...</div>;

  const locked = appt.status === "firmato" || !can(user, "appt_write");
  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, transport_cost: Number(form.transport_cost) || 0, items: form.items.map((i) => ({ ...i, price: Number(i.price) || 0 })) };
      const { data } = await api.put(`/appointments/${id}`, payload);
      setAppt(data);
      setDirty(false);
      toast.success("Preventivo salvato");
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo appuntamento?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Appuntamento eliminato");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={`space-y-6 ${dirty ? "pb-24" : ""}`}>
      <div className="flex items-start justify-between gap-3 fade-up">
        <div className="space-y-2 min-w-0">
          <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-[var(--muted)] hover:text-[var(--ink)]" data-testid="back-button"><ArrowLeft className="h-4 w-4" /> Appuntamenti</button>
          <h1 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight truncate" data-testid="appointment-title">{appt.client_name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={appt.status} />
            {appt.assigned_name && <span className="text-xs text-[var(--muted)]" data-testid="appointment-assigned-name">Assegnato a {appt.assigned_name}</span>}
          </div>
        </div>
        {user.role === "admin" && <button onClick={remove} className="btn-ghost text-red-600" data-testid="delete-appointment-button"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <Tabs defaultValue={locked ? "firma" : "oggetti"}>
        <TabsList className="bg-[var(--surface-2)] h-11 p-1 w-full sm:w-auto">
          <TabsTrigger value="dati" className="flex-1 sm:flex-none h-9" data-testid="tab-dati">Dati</TabsTrigger>
          <TabsTrigger value="oggetti" className="flex-1 sm:flex-none h-9" data-testid="tab-oggetti">Oggetti & prezzi</TabsTrigger>
          <TabsTrigger value="firma" className="flex-1 sm:flex-none h-9" data-testid="tab-firma">Riepilogo & firma</TabsTrigger>
        </TabsList>
        <TabsContent value="dati" className="mt-6"><ClientForm form={form} update={update} locked={locked} /></TabsContent>
        <TabsContent value="oggetti" className="mt-6">
          <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
            <ItemsEditor items={form.items} onChange={(items) => update({ items })} locked={locked} />
            <TotalsCard items={form.items} transport={form.transport_cost} pct={pct} locked={locked} onTransport={(v) => update({ transport_cost: v })} />
          </div>
        </TabsContent>
        <TabsContent value="firma" className="mt-6">
          <ContractSection appt={appt} dirty={dirty} onSave={save} onSigned={setAppt} readOnly={!can(user, "appt_write")} />
        </TabsContent>
      </Tabs>
      {!locked && dirty && (
        <div className="fixed bottom-20 lg:bottom-6 inset-x-4 lg:left-auto lg:right-10 lg:w-auto z-30 flex justify-center fade-up">
          <button onClick={save} disabled={saving} className="btn-primary h-12 px-6 shadow-xl" data-testid="save-appointment-button">
            <Save className="h-5 w-5" /> {saving ? "Salvataggio..." : "Salva modifiche"}
          </button>
        </div>
      )}
    </div>
  );
}
